/* global fetch */
import {objectToQueryString} from './utils';
import config, {isExperiment} from '../config';
import log from './log';
import _ from 'lodash';


/**
 * Request only mandatory fields to reduce API response time
 */

export const APPS_PER_PAGE = 48;

const fetchTimeout = typeof window === 'object' ? 15000 : 5000;

const fields = 'appDefinitionId,appType,by,downloadsAllTime,externalPremium,frontPageImage,hasDashboard,hasMobile,hasPremium,isTrial,isWixLabs,name,premiumOnly,publishedAt,slug,teaser,widgets,wixComponentId,isWixComponent,appType,hasSection,dashboard,overrideDashboardUrl,listWidgetId,price,hideAppFirstTimeMsg,roundIcon,appIcon,overrideDashboardLink,listedInMarket';
const exclude = 'editor_1.4_incompatible,ecommerce_old';

const responseCacheDuration = 30 * 60000;
const responseCache = {};

function responseCacheGet(url) {
  if (responseCache[url] && responseCache[url].receivedAt + responseCacheDuration > (new Date()).getTime()) {
    return JSON.parse(JSON.stringify(responseCache[url].data));
  }
}

function responseCacheSet(url, resp, cacheAble) {
  if (cacheAble && typeof window !== 'object' && JSON.stringify(resp).length > 4 && Object.keys(responseCache).length < 200) {
    responseCache[url] = {data: resp, receivedAt: (new Date()).getTime()};
  }
}

export function getAppBySlug(slug, lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps/${slug}/priced-app`, {lang}, {});
}

export function getAppsByIdMap(idMap, lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps/dashboard`, {q: idMap.join(','), lang});
}

export function getPopularApps(lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps/featuredCat`, {exclude, lang, fields}, {}, true);
}

export function getAppsByCategory(category, lang, offset = 0, cfg) {
  // Include dashboard apps only in "All Apps" category and for Premium users. undefined means not filter.
  const dashboard = (category === 'all-apps' || cfg.isPremium) ? undefined : false;
  const params = {dashboard, lang, limit: APPS_PER_PAGE, offset, exclude, fields};
  if (isExperiment('specs.market.PositionBiasTest', cfg.experiments)) {
    params.positionBiasTest = true;
  }
  return doFetch(`${config.topology.appMarketApiUrl}/categories/${category}/apps`, params, {}, true);
}

export function getCategories(market, lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/categories/`, {lang, market}, {}, true);
}

// TODO remove these 5 when RedirectToOneAppMarket merged
export function getFeaturedApps(lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps/featuredCat`, {dashboard: false, exclude, lang, fields}, {}, true);
}
export function getNewApps(lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps/new`, {exclude, lang, fields, limit: 12}, {}, true);
}
export function getMostPopularApps(lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps/mostPopularCat`, {exclude, lang, fields, dashboard: false, limit: 12}, {}, true);
}
export function getBusinessApps(lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps`, {exclude, lang, fields, dashboard: true, offset: 0, limit: 20}, {}, true);
}
export function getAllApps(lang, offset) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps`, {exclude, lang, fields, dashboard: false, offset, limit: APPS_PER_PAGE}, {}, true);
}

export function getCollection(vertical, lang) {
  return doFetch(`${config.topology.appMarketApiUrl}/categories/${vertical}`, {lang, market: 'editor'}, {}, true);
}

export function getCollectionApps(vertical, lang, experiments) {
  const params = {dashboard: false, lang, limit: 5, exclude, fields};
  if (isExperiment('specs.market.PositionBiasTest', experiments)) {
    params.positionBiasTest = true;
  }
  return doFetch(`${config.topology.appMarketApiUrl}/categories/${vertical}/apps`, params, {}, true);
}

export function getSearch(query, lang, limit = 48) {
  return doFetch(`${config.topology.appMarketApiUrl}/apps/`, {filter: query, lang, limit, exclude, fields});
}

export function getAllReviews() {
  return doFetch(`${config.topology.reviewsApiUrl}/v1/products`);
}

export function getPendingApps(metaSite) {
  return doFetch(`${config.topology.wixAppMarketUrl}/apps/pending/${metaSite}`);
}

export function removePendingApp(metaSite, appDefinitionId) {
  const options = {
    method: 'DELETE'
  };
  return doFetch(`${config.topology.wixAppMarketUrl}/apps/pending/${metaSite}/${appDefinitionId}`, {}, options);
}

/**
 * Credentials do not work on server side - add cookie manually
 */
export function getSiteProfile(metaSite, cookie) {
  const options = {headers: {cookie}};
  const query = {fields: 'category,first_premium_site_id', accept: 'json'};

  const transform = (json) => {
    const data = json.success ? _.keyBy(json.payload.fields, 'name') : {};
    return {
      vertical: json.success ? data.category.value : 'other',
      metaData: json.success ? {...json.payload.metadata} : null,
      isSitePremium: json.success ? !!data.first_premium_site_id.value : false,
      receivedAt: json.success ? Date.now() : 0
    };
  };

  if (metaSite) {
    return doFetch(`${config.topology.biProfileServerUrl}/siteprofile/${metaSite}`, query, options)
      .then((resp) => transform(resp))
      .catch(() => transform({}));

  } else {
    return Promise.resolve(transform({}));
  }
}

/**
 * Credentials do not work on server side - add cookie manually
 */
export function getMyAppsFromMetaSite(metaSite, cookie) {
  const transform = (json) => {
    return {
      items: json.success ? json.payload.map(i => i) : [],
      receivedAt: json.success ? Date.now() : 0
    };
  };

  if (metaSite) {
    return doFetch(`${config.topology.metaSiteWebAppUrl}/metaSite/${metaSite}/embeddedService/`, {}, {headers: {cookie}})
      .then((resp) => transform(resp))
      .catch(() => transform({}));

  } else {
    return Promise.resolve(transform({}));
  }
}

export function getExperiments(petri) {
  return Promise.all([
    petri.conductAllInScope('santa-editor'),
    petri.conductAllInScope('app-market-editor')
  ])
    .then((expArr) => Object.assign({}, expArr[0], expArr[1]))
    .then((experiments) => {
      log.debug(`Experiments count: ${Object.keys(experiments).length} PositionExp: ${experiments['specs.market.PositionBiasTest']}`);
      return experiments;
    });
}

/**
 * Don't use doFetch because api returns 500 error for regular case, not installed app
 */
export function getIfAppIsInstalledFromMetaSite(metaSite, appDefinitionId, cookie) {
  return fetch(`${config.topology.metaSiteWebAppUrl}/metaSite/${metaSite}/embeddedService/${appDefinitionId}`, {headers: {cookie}})
    .then((resp) => resp.status === 200);
}


export function postProvisionAppToMetaSite(metaSite, appDefinitionId, referral) {
  const options = {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({params: {referralInfo: referral}})
  };
  return doFetch(`${config.topology.metaSiteWebAppUrl}/metaSite/${metaSite}/embeddedService/${appDefinitionId}`, {}, options);
}


function doFetch(url, query = {}, options = {}, cacheAble) {
  query = objectToQueryString(query);
  query = query.length ? `?${query}` : '';
  const _url = `${url}${query}`;
  let timeoutId;

  const fromCache = responseCacheGet(_url);
  if (fromCache) {
    return Promise.resolve(fromCache);
  }

  return Promise.race([
    fetch(_url, options)
    .then(resp => {
      if (resp.status >= 200 && resp.status < 300) {
        return resp.json()
          .catch(() => {}); // in case of empty or wrong json response
      } else {
        const err = new Error(`Unexpected response code: ${resp.status}`);
        err._responseStatus = resp.status;
        throw err;
      }
    }),
    new Promise((resolve, reject) => {
      timeoutId = setTimeout(() => {
        timeoutId = null;
        reject(new Error(`Forced Timeout ${fetchTimeout}ms`));
      }, fetchTimeout);
    })
  ])
    .catch(err => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      log.error(`Call to "${_url}" failed. ${err}`);
      throw err;
    })
    .then(resp => {
      clearTimeout(timeoutId);
      responseCacheSet(_url, resp, cacheAble);
      return resp;
    });
}



// WEBPACK FOOTER //
// ./services/market-api.js